import { writable, derived, get } from 'svelte/store';
import type { PHR } from './types';
import { authUser } from './firebase';
import {
  emptyPHR, loadPHR, addRecord, updateRecord, deleteRecord, listOwnProfiles, listSharedProfiles, createProfile,
  updateProfileDoc, addGuardian, addProvider, removeProvider, removeGuardian, VITAL_FIELD, type RecordKind, type Profile,
} from './phr';
import { uploadImport, listImports, type ImportRecord } from './imports';
import { logAccess, listAccess, type AccessEntry } from './access';
import { hasAcceptedAgreement, recordAcceptance, listAcceptances, type Acceptance } from './agreement';
import { listMovies, addMovieDoc, uploadMovieBlob, removeMovie } from './movies';
import type { StudyMovie } from './types';
import { diseaseKey } from './conditionDedupe';
import { ensureEmpoweredId } from './account';
import { loadProfilePrefs, saveProfilePrefs, loadAppPrefs, saveAppPrefs, type ProfilePrefs, type AppPrefs } from './prefs';
import { DEFAULT_LABS, MAX_LABS } from './labCatalog';
import { colKey, parseDate } from './labTable';
import { isDemo, setDemo, demoActive, demoProfilesList, demoUser, demoPrefs, demoLoadPHR, logDemoEvent, fetchDemoUsage } from './demoMode';

export type TabId = 'dashboard' | 'profile' | 'labs' | 'imaging' | 'procedures' | 'notes';
export const activeTab = writable<TabId>('dashboard');

// Cross-tab deep-link: Wellness sets this to send the viewer to an imaging /
// procedure record and auto-open its detail there (no detail is duplicated in
// Wellness). The target view consumes it and resets it to null.
export const focusRecord = writable<{ kind: 'imaging' | 'procedures'; id: string } | null>(null);

export const chatOpen = writable<boolean>(false);
export const settingsOpen = writable<boolean>(false);
export const uploadsOpen = writable<boolean>(false);               // uploads/imports modal (from Settings)
export const profileFormOpen = writable<boolean>(false);
export const profileEditTarget = writable<Profile | null>(null);   // null = add new; set = edit

// ── Font preference ────────────────────────────────────────────────────────────
export type FontChoice = 'jakarta' | 'helvetica' | 'crimson';
const FONT_KEY = 'mpwrd.font';
function initialFont(): FontChoice {
  try {
    const f = localStorage.getItem(FONT_KEY);
    if (f === 'orbitron') return 'helvetica';   // Orbitron was replaced by Helvetica
    if (f === 'gotham' || f === 'saira' || f === 'rubik' || f === 'forum') return 'jakarta';   // retired options → default
    if (f === 'jakarta' || f === 'helvetica' || f === 'crimson') return f;
  } catch (_) {}
  return 'jakarta';
}
export const font = writable<FontChoice>(initialFont());
font.subscribe((f) => {
  try { document.documentElement.setAttribute('data-font', f); localStorage.setItem(FONT_KEY, f); } catch (_) {}
});

// ── Font size — uniform scale. Level 0..3 → +10% per step (Default → XX-Large). ──
const FSCALE_KEY = 'mpwrd.fontScale';
export const FONT_SCALE_LEVELS = [
  { level: 0, label: 'Default', scale: 1 },
  { level: 1, label: 'Large', scale: 1.1 },
  { level: 2, label: 'X-Large', scale: 1.21 },
  { level: 3, label: 'XX-Large', scale: 1.331 },
];
function initialFontScale(): number {
  try { const n = Number(localStorage.getItem(FSCALE_KEY)); if (Number.isInteger(n) && n >= 0 && n <= 3) return n; } catch (_) {}
  return 0;
}
export const fontScale = writable<number>(initialFontScale());   // the LEVEL (0..3)
fontScale.subscribe((lvl) => {
  const s = FONT_SCALE_LEVELS[lvl]?.scale ?? 1;
  try { document.documentElement.style.setProperty('--fs-scale', String(s)); localStorage.setItem(FSCALE_KEY, String(lvl)); } catch (_) {}
});

// ── Theme — dark (default), light, or system (follow the OS). Applied as
// html[data-theme]; the light token set lives in app.css. ──────────────────────────
export type ThemeChoice = 'dark' | 'light' | 'system';
const THEME_KEY = 'mpwrd.theme';
function initialTheme(): ThemeChoice {
  try { const t = localStorage.getItem(THEME_KEY); if (t === 'light' || t === 'dark' || t === 'system') return t; } catch (_) {}
  return 'dark';
}
export const theme = writable<ThemeChoice>(initialTheme());
function prefersLight(): boolean { try { return window.matchMedia('(prefers-color-scheme: light)').matches; } catch { return false; } }
function applyTheme(t: ThemeChoice): void {
  const eff = t === 'system' ? (prefersLight() ? 'light' : 'dark') : t;
  try {
    document.documentElement.setAttribute('data-theme', eff);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', eff === 'light' ? '#FAF8F3' : '#0E1116');
  } catch (_) {}
}
theme.subscribe((t) => { applyTheme(t); try { localStorage.setItem(THEME_KEY, t); } catch (_) {} });
try { window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => { if (get(theme) === 'system') applyTheme('system'); }); } catch (_) {}

// ── Account-level appearance sync (per USER): theme, font + text size follow the
// signed-in person across devices (stored on users/{uid}.app). localStorage stays the
// instant cache; the account is authoritative once signed in. ──────────────────────
let appUid: string | null = null;
let appPrefsLoading = false;
let appSaveTimer: ReturnType<typeof setTimeout> | null = null;
async function loadAppPrefsFor(u: string): Promise<void> {
  appUid = u; appPrefsLoading = true;
  let seed = false;
  try {
    const p = await loadAppPrefs(u);
    if (appUid === u && p && Object.keys(p).length) {
      if (p.font === 'jakarta' || p.font === 'helvetica' || p.font === 'crimson') font.set(p.font);
      if (typeof p.fontScale === 'number' && Number.isInteger(p.fontScale) && p.fontScale >= 0 && p.fontScale <= 3) fontScale.set(p.fontScale);
      if (p.theme === 'dark' || p.theme === 'light' || p.theme === 'system') theme.set(p.theme);
    } else if (appUid === u) seed = true;
  } finally {
    if (appUid === u) { appPrefsLoading = false; if (seed) persistAppPrefs(); }
  }
}
function resetAppPrefs(): void { appUid = null; }
function persistAppPrefs(): void {
  if (appPrefsLoading || !appUid) return;
  const u = appUid, patch: AppPrefs = { font: get(font), fontScale: get(fontScale), theme: get(theme) };
  if (appSaveTimer) clearTimeout(appSaveTimer);
  appSaveTimer = setTimeout(() => { if (appUid === u) saveAppPrefs(u, patch); }, 700);
}
font.subscribe(persistAppPrefs);
fontScale.subscribe(persistAppPrefs);
theme.subscribe(persistAppPrefs);

// ── Per-profile lab-view preferences (pinned dashboard labs, extra Trends markers,
// Trends chart order) ────────────────────────────────────────────────────────────
// Stored per patient in Firestore (users/{ownerUid}/profiles/{pid}/prefs/labView) so
// they follow the user across devices and are scoped to each profile. localStorage is
// a per-profile cache for instant paint; Firestore is authoritative, loaded when a
// profile becomes active. To add a preference: add a key to ProfilePrefs (prefs.ts)
// and a line to applyPrefs()/snapshot() below — nothing else changes.
export const dashboardLabs = writable<string[]>(DEFAULT_LABS);   // ≤4 pinned to the dashboard
export const trendsLabs = writable<string[]>([]);                // ≤4 extra Trends markers
export const trendsOrder = writable<string[]>([]);               // user-arranged Trends order

const prefsCacheKey = (ownerUid: string, pid: string) => `mpwrd.prefs.${ownerUid}/${pid}`;
// Legacy device-global keys (pre per-profile): seed a profile's prefs the first time so
// existing users keep their layout, then each profile diverges.
function readLegacyPrefs(): ProfilePrefs {
  const arr = (k: string) => { try { const a = JSON.parse(localStorage.getItem(k) || 'null'); return Array.isArray(a) ? a.filter((x) => typeof x === 'string') : undefined; } catch { return undefined; } };
  return { dashLabs: arr('mpwrd.dashLabs'), trendsLabs: arr('mpwrd.trendsLabs'), trendsOrder: arr('mpwrd.trendsOrder') };
}
function readPrefsCache(ownerUid: string, pid: string): ProfilePrefs | null {
  try { const raw = localStorage.getItem(prefsCacheKey(ownerUid, pid)); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function snapshotPrefs(): ProfilePrefs {
  return { dashLabs: get(dashboardLabs), trendsLabs: get(trendsLabs), trendsOrder: get(trendsOrder) };
}
function applyPrefs(p: ProfilePrefs) {
  dashboardLabs.set(Array.isArray(p.dashLabs) && p.dashLabs.length ? p.dashLabs.slice(0, MAX_LABS) : DEFAULT_LABS);
  trendsLabs.set(Array.isArray(p.trendsLabs) ? p.trendsLabs.slice(0, 4) : []);
  trendsOrder.set(Array.isArray(p.trendsOrder) ? p.trendsOrder : []);
}

let prefsCtx: { ownerUid: string; pid: string } | null = null;
let prefsLoading = false;
let prefsSaveTimer: ReturnType<typeof setTimeout> | null = null;
const prefsActive = (o: string, p: string) => !!prefsCtx && prefsCtx.ownerUid === o && prefsCtx.pid === p;

/** Load a profile's preferences into the stores when it becomes active. */
async function loadPrefsFor(ownerUid: string, pid: string): Promise<void> {
  prefsCtx = { ownerUid, pid };
  prefsLoading = true;
  applyPrefs(readPrefsCache(ownerUid, pid) ?? readLegacyPrefs());   // instant paint
  let seed = false;
  try {
    const remote = await loadProfilePrefs(ownerUid, pid);
    if (prefsActive(ownerUid, pid)) {
      if (remote && Object.keys(remote).length) applyPrefs(remote);   // authoritative
      else seed = true;                                               // first run → migrate current layout up
    }
  } finally {
    if (prefsActive(ownerUid, pid)) { prefsLoading = false; if (seed) persistPrefs(); }
  }
}
function resetPrefs(): void {
  prefsCtx = null; prefsLoading = true; applyPrefs({}); prefsLoading = false;
}
/** Persist current stores → per-profile cache (instant) + Firestore (debounced). */
function persistPrefs(): void {
  if (prefsLoading || !prefsCtx) return;
  const { ownerUid, pid } = prefsCtx, p = snapshotPrefs();
  try { localStorage.setItem(prefsCacheKey(ownerUid, pid), JSON.stringify(p)); } catch (_) {}
  if (prefsSaveTimer) clearTimeout(prefsSaveTimer);
  prefsSaveTimer = setTimeout(() => { if (prefsActive(ownerUid, pid)) saveProfilePrefs(ownerUid, pid, p); }, 700);
}
dashboardLabs.subscribe(persistPrefs);
trendsLabs.subscribe(persistPrefs);
trendsOrder.subscribe(persistPrefs);

// ── Auth + multi-profile (owned + shared) patient record ───────────────────────
// In demo mode there is no Firebase session; the "signed-in user" is a synthetic
// caregiver that owns every demo profile, so ownership/permission derived stores
// (isOwnerOfActive / canManageActive) resolve correctly. `user` swaps to it when
// demoActive flips; the real authUser is used everywhere else.
// The synthetic user comes from the demo dataset (family caregiver, or the patient
// themself for a console-issued physician demo) — see demoMode.setDemoDataset.
export const user = derived([authUser, demoActive], ([$au, $demo]) => ($demo ? (demoUser() as any) : $au));
export const authReady = writable<boolean>(false);
export const dataReady = writable<boolean>(false);
export const loading = writable<boolean>(false);
export const phr = writable<PHR>(emptyPHR());
export const imports = writable<ImportRecord[]>([]);
export const studyMovies = writable<StudyMovie[]>([]);
// null = still checking, false = must accept the sign-on agreement, true = accepted.
export const ndaAccepted = writable<boolean | null>(null);
export const profiles = writable<Profile[]>([]);
export const activeProfileId = writable<string | null>(null);
export const empoweredId = writable<string | null>(null);   // unique account ID (6-char)

/** The active Profile object (own or shared), for UI that needs ownerUid/guardians. */
export const activeProfile = derived([profiles, activeProfileId], ([$p, $id]) => $p.find((x) => x.id === $id) ?? null);

let uid: string | null = null;
let email: string | null = null;
let displayName: string | null = null;
let activeOwnerUid: string | null = null;
let activePid: string | null = null;
activeProfileId.subscribe((v) => { activePid = v; });

const activeKey = () => `mpwrd.activeProfile.${uid}`;
function setActive(ownerUid: string, pid: string) {
  activeOwnerUid = ownerUid;
  activeProfileId.set(pid);
  try { localStorage.setItem(activeKey(), `${ownerUid}/${pid}`); } catch (_) {}
  // Audit: record that this signed-in user opened the profile (once per session).
  if (uid) logAccess(ownerUid, pid, { uid, email: email || '', name: displayName || '', isOwner: ownerUid === uid });
  // Load this profile's saved lab-view layout (pinned labs, extras, chart order).
  void loadPrefsFor(ownerUid, pid);
}

/** Access history for the active profile over the last `days` days (owner only). */
export async function loadAccessHistory(days = 30): Promise<AccessEntry[]> {
  if (isDemo()) return [];
  if (!activeOwnerUid || !activePid) return [];
  if (activeOwnerUid !== uid) return [];   // only the owner may read the audit trail
  return listAccess(activeOwnerUid, activePid, days);
}
export type { AccessEntry } from './access';

// Default profile: the one auto-selected on login (overrides last-used). Per
// account, persisted locally.
const defaultKey = () => `mpwrd.defaultProfile.${uid}`;
export const defaultProfileId = writable<string | null>(null);
export function setDefaultProfile(pid: string | null): void {
  defaultProfileId.set(pid);
  try { if (pid) localStorage.setItem(defaultKey(), pid); else localStorage.removeItem(defaultKey()); } catch (_) {}
}

authUser.subscribe(async (u) => {
  if (isDemo()) { authReady.set(true); return; }   // demo shell owns uid/profiles/phr
  uid = u?.uid ?? null;
  email = u?.email ?? null;
  displayName = u?.displayName ?? null;
  authReady.set(true);
  if (!uid) { profiles.set([]); activeProfileId.set(null); activeOwnerUid = null; phr.set(emptyPHR()); imports.set([]); empoweredId.set(null); ndaAccepted.set(null); resetPrefs(); resetAppPrefs(); dataReady.set(true); return; }
  // Appearance (theme / font / text size) follows the account across devices.
  void loadAppPrefsFor(uid);
  // Must accept the sign-on agreement before anything loads.
  ndaAccepted.set(null);
  hasAcceptedAgreement(uid).then((ok) => ndaAccepted.set(ok)).catch(() => ndaAccepted.set(false));
  dataReady.set(false);
  try { await loadProfiles(); } finally { dataReady.set(true); }
  // Mint (or fetch) the account's unique empowered ID — non-blocking.
  ensureEmpoweredId().then((id) => empoweredId.set(id)).catch(() => {});
});

// Safety net for the boot spinner (App.svelte keys "Loading…" off authReady). If the
// Auth SDK never reports an initial state — seen on iOS Safari when a pending
// redirect's storage is partitioned away — flip authReady after a few seconds so the
// app falls through to the sign-in screen instead of hanging on "Loading…" forever.
setTimeout(() => authReady.set(true), 8000);

async function loadProfiles(): Promise<void> {
  if (!uid) return;
  const own = await listOwnProfiles(uid);
  let shared: Profile[] = [];
  try { if (email) shared = await listSharedProfiles(email, uid); } catch (_) {}
  const list = [...own, ...shared];
  profiles.set(list);
  if (!list.length) { activeProfileId.set(null); activeOwnerUid = null; defaultProfileId.set(null); phr.set(emptyPHR()); imports.set([]); return; }
  let def: string | null = null;
  try { def = localStorage.getItem(defaultKey()); } catch (_) {}
  if (def && !list.some((x) => x.id === def)) def = null;   // default profile no longer exists
  defaultProfileId.set(def);
  let chosen: Profile | null = null;
  if (def) chosen = list.find((x) => x.id === def) ?? null;   // a default profile wins on login
  else if (list.length === 1) chosen = list[0];              // only one profile → no choice needed
  // else: multiple profiles + no default → require an explicit pick at login (ProfilePicker).
  if (chosen) { setActive(chosen.ownerUid, chosen.id); await refresh(); }
  else { activeProfileId.set(null); activeOwnerUid = null; phr.set(emptyPHR()); imports.set([]); }
}

export async function refresh(): Promise<void> {
  if (!activeOwnerUid || !activePid) return;
  if (isDemo()) { phr.set(demoLoadPHR(activePid)); return; }
  loading.set(true);
  try {
    const [p, imp, mv] = await Promise.all([
      loadPHR(activeOwnerUid, activePid),
      listImports(activeOwnerUid, activePid),
      listMovies(activeOwnerUid, activePid).catch(() => [] as StudyMovie[]),
    ]);
    phr.set(p); imports.set(imp); studyMovies.set(mv);
  } finally { loading.set(false); }
}

/** Record the current user's acceptance of the sign-on agreement, then unblock. */
export async function acceptAgreement(): Promise<void> {
  if (!uid) throw new Error('Not signed in');
  await recordAcceptance(uid, email || '');
  ndaAccepted.set(true);
}

/** All agreement versions the current user has accepted (for Settings display). */
export async function loadAgreementAcceptances(): Promise<Acceptance[]> {
  if (isDemo()) return [];
  if (!uid) return [];
  return listAcceptances(uid);
}
export type { Acceptance } from './agreement';

/** Reload just the movies list for the active profile. */
export async function refreshMovies(): Promise<void> {
  if (!activeOwnerUid || !activePid) return;
  studyMovies.set(await listMovies(activeOwnerUid, activePid).catch(() => []));
}

/** Upload a movie/key-image blob (+ optional thumbnail) and link it to a study. */
export async function saveStudyMovie(
  studyKey: string,
  m: { name: string; desc?: string; kind: 'movie' | 'image'; blob: Blob; thumb?: Blob | null; frames?: number; fps?: number },
): Promise<void> {
  if (isDemo()) throw new Error(DEMO_BLOCK);
  if (!activeOwnerUid || !activePid) throw new Error('No active profile');
  const ext = m.kind === 'movie' ? 'mp4' : 'jpg';
  const base = `${Date.now().toString(36)}-${(m.name || 'clip').replace(/[^A-Za-z0-9]+/g, '-').slice(0, 40)}`;
  const storagePath = await uploadMovieBlob(activeOwnerUid, activePid, studyKey, `${base}.${ext}`, m.blob);
  let thumbPath: string | undefined;
  if (m.thumb) thumbPath = await uploadMovieBlob(activeOwnerUid, activePid, studyKey, `${base}-thumb.jpg`, m.thumb);
  await addMovieDoc(activeOwnerUid, activePid, {
    studyKey, name: m.name, desc: m.desc, kind: m.kind,
    storagePath, thumbPath, contentType: m.blob.type, frames: m.frames, fps: m.fps, size: m.blob.size,
  });
  await refreshMovies();
}

/** Remove a movie/key image (Firestore link + Storage blobs). */
export async function deleteStudyMovie(m: StudyMovie): Promise<void> {
  if (isDemo()) throw new Error(DEMO_BLOCK);
  if (!activeOwnerUid || !activePid) throw new Error('No active profile');
  await removeMovie(activeOwnerUid, activePid, m);
  await refreshMovies();
}

/** Archive + log an uploaded file (immutable) to the active profile. */
export async function addImport(file: File): Promise<ImportRecord | null> {
  if (isDemo()) throw new Error(DEMO_BLOCK);
  if (!activeOwnerUid || !activePid) throw new Error('No active profile');
  const id = await uploadImport(activeOwnerUid, activePid, file, email || 'self');
  const list = await listImports(activeOwnerUid, activePid);
  imports.set(list);
  return list.find((r) => r.id === id) ?? null;
}

export async function switchProfile(pid: string): Promise<void> {
  const p = get(profiles).find((x) => x.id === pid);
  if (!p) return;
  if (isDemo()) { activateDemo(p.ownerUid, p.id); logDemoEvent('profile', `${p.firstName} ${p.lastName}`.trim()); return; }
  setActive(p.ownerUid, p.id);
  await refresh();
}

// ── Demo mode ────────────────────────────────────────────────────────────────────
// Entered from /demo after a valid test code (see App.svelte / DemoGate). Seeds the
// shell from the in-memory synthetic dataset; the PHR data layer (phr.ts) routes all
// reads/writes to demoMode.ts, so navigation AND in-memory edits work with no login.
export function startDemo(): void {
  if (get(demoActive)) return;
  setDemo(true);
  const du = demoUser(), list = demoProfilesList();
  uid = du.uid; email = du.email; displayName = du.displayName;
  empoweredId.set('DEMO01');
  ndaAccepted.set(true);
  defaultProfileId.set(null);
  profiles.set(list);
  activateDemo(list[0].ownerUid, list[0].id);
  const pr = demoPrefs();                                   // dataset-pinned dashboard labs (e.g. the diabetes preset)
  if (pr.dashLabs?.length) dashboardLabs.set(pr.dashLabs.slice(0, MAX_LABS));
  authReady.set(true);
  dataReady.set(true);
  logDemoEvent('started');
  void fetchDemoUsage();
}
function activateDemo(ownerUid: string, pid: string): void {
  activeOwnerUid = ownerUid;
  activeProfileId.set(pid);            // also updates activePid via its subscription
  phr.set(demoLoadPHR(pid));
  imports.set([]);
  studyMovies.set([]);
  resetPrefs();                        // default lab-view layout (no Firestore)
}
/** In-memory profile-demographics edit for the demo (persists for the session). */
function demoPatchProfile(pid: string, data: Partial<Profile>): void {
  profiles.update((list) => list.map((p) => (p.id === pid ? { ...p, ...data } : p)));
}
const DEMO_BLOCK = 'This action is turned off in the demo. Sign in to use it with your own record.';

export async function createNewProfile(data: Omit<Profile, 'id' | 'ownerUid'>, linkLoginEmail = ''): Promise<string> {
  if (isDemo()) throw new Error(DEMO_BLOCK);
  if (!uid || !email) throw new Error('Not signed in');
  const ownerName = displayName || email;   // the creating parent/guardian manages this profile
  const pid = await createProfile(uid, email, { ...data, ownerName });
  // Link a child/dependent's OWN Google login so they land on this record (no signup).
  const e = linkLoginEmail.trim().toLowerCase();
  if (e && e !== (email || '').toLowerCase()) {
    try { await addGuardian(uid, pid, e, `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim()); }
    catch (err) { console.error('link child login failed', err); }
  }
  setActive(uid, pid);
  await loadProfiles();
  return pid;
}

/** Link a dependent's own Google login to an existing profile they'll sign in with.
 *  Grants guardian-level (read + add) access via the shared-profile discovery index. */
export async function linkChildLogin(ownerUid: string, pid: string, childEmail: string): Promise<void> {
  if (isDemo()) throw new Error(DEMO_BLOCK);
  if (!uid) throw new Error('Not signed in');
  if (!manages(pid)) throw new Error('You do not have permission to manage this record.');
  const e = childEmail.trim().toLowerCase();
  if (!e || e === (email || '').toLowerCase()) return;
  const p = get(profiles).find((x) => x.id === pid);
  const name = p ? `${p.firstName} ${p.lastName}`.trim() : '';
  await addGuardian(ownerUid, pid, e, name);
  await loadProfiles();
}

export async function updateActiveProfile(data: Partial<Profile>): Promise<void> {
  if (!activeOwnerUid || !activePid) throw new Error('No active profile');
  if (isDemo()) { demoPatchProfile(activePid, data); return; }
  await updateProfileDoc(activeOwnerUid, activePid, data);
  await loadProfiles();
}

/** Edit any profile (badge edit). */
export async function updateProfileById(ownerUid: string, pid: string, data: Partial<Profile>): Promise<void> {
  if (isDemo()) { demoPatchProfile(pid, data); return; }
  await updateProfileDoc(ownerUid, pid, data);
  await loadProfiles();
}

/** Grant another parent/guardian access to the active profile (owner only). */
export async function addGuardianToActive(guardianEmail: string): Promise<void> {
  if (isDemo()) throw new Error(DEMO_BLOCK);
  if (!activeOwnerUid || !activePid) throw new Error('No active profile');
  if (!manages(activePid)) throw new Error('You do not have permission to manage this record.');
  const p = get(profiles).find((x) => x.id === activePid);
  const name = p ? `${p.firstName} ${p.lastName}`.trim() : '';
  await addGuardian(activeOwnerUid, activePid, guardianEmail, name);
  await loadProfiles();
}

/** Grant a service provider (doctor) access to the active profile (owner only). */
export async function addProviderToActive(providerEmail: string): Promise<void> {
  if (isDemo()) throw new Error(DEMO_BLOCK);
  if (!activeOwnerUid || !activePid) throw new Error('No active profile');
  if (!manages(activePid)) throw new Error('You do not have permission to manage this record.');
  const p = get(profiles).find((x) => x.id === activePid);
  const name = p ? `${p.firstName} ${p.lastName}`.trim() : '';
  await addProvider(activeOwnerUid, activePid, providerEmail, name);
  await loadProfiles();
}

/** Revoke a service provider's access to the active profile (owner only). */
export async function removeProviderFromActive(providerEmail: string): Promise<void> {
  if (isDemo()) throw new Error(DEMO_BLOCK);
  if (!activeOwnerUid || !activePid) throw new Error('No active profile');
  if (!manages(activePid)) throw new Error('You do not have permission to manage this record.');
  await removeProvider(activeOwnerUid, activePid, providerEmail);
  await loadProfiles();
}

/** Revoke a guardian's access to the active profile (owner only). */
export async function removeGuardianFromActive(guardianEmail: string): Promise<void> {
  if (isDemo()) throw new Error(DEMO_BLOCK);
  if (!activeOwnerUid || !activePid) throw new Error('No active profile');
  if (!manages(activePid)) throw new Error('You do not have permission to manage this record.');
  await removeGuardian(activeOwnerUid, activePid, guardianEmail);
  await loadProfiles();
}

/** Grant a service provider access to a SPECIFIC owned profile (per-child sharing).
 *  Access is scoped to that one profile only — other children are not shared. */
export async function addProviderToProfile(pid: string, providerEmail: string): Promise<void> {
  if (isDemo()) throw new Error(DEMO_BLOCK);
  if (!uid) throw new Error('Not signed in');
  const p = get(profiles).find((x) => x.id === pid);
  if (!p) throw new Error('Profile not found');
  if (!manages(pid)) throw new Error('You do not have permission to manage this record.');
  await addProvider(p.ownerUid, pid, providerEmail, `${p.firstName} ${p.lastName}`.trim());
  await loadProfiles();
}

/** Revoke a service provider's access to a SPECIFIC owned profile (per-child). */
export async function removeProviderFromProfile(pid: string, providerEmail: string): Promise<void> {
  if (isDemo()) throw new Error(DEMO_BLOCK);
  if (!uid) throw new Error('Not signed in');
  const p = get(profiles).find((x) => x.id === pid);
  if (!p) throw new Error('Profile not found');
  if (!manages(pid)) throw new Error('You do not have permission to manage this record.');
  await removeProvider(p.ownerUid, pid, providerEmail);
  await loadProfiles();
}

export async function addPhrRecord(kind: RecordKind, data: Record<string, unknown>): Promise<string> {
  if (!activeOwnerUid || !activePid) throw new Error('No active profile');
  const id = await addRecord(activeOwnerUid, activePid, kind, data);
  await refresh();
  return id;
}

/** Add a condition — but if the SAME disease is already tracked, consolidate into
 *  that condition (upgrade status, fill region/system/onset) instead of a new doc. */
export async function addOrMergeCondition(data: Record<string, unknown>): Promise<'added' | 'merged'> {
  if (!activeOwnerUid || !activePid) throw new Error('No active profile');
  const RANK: Record<string, number> = { Active: 0, Chronic: 1, Resolved: 2 };
  const key = diseaseKey(String(data.description ?? ''));
  const existing = get(phr).conditions.find((c) => diseaseKey(c.description) === key);
  if (existing && existing._id) {
    const fields: Record<string, unknown> = {};
    if ((RANK[String(data.status)] ?? 3) < (RANK[existing.status] ?? 3)) fields.status = data.status;
    if (!existing.region && data.region) fields.region = data.region;
    if (!existing.system && data.system) fields.system = data.system;
    if (data.since && !existing.since) fields.since = data.since;
    if (Object.keys(fields).length) { await updateRecord(activeOwnerUid, activePid, 'conditions', existing._id, fields); await refresh(); }
    return 'merged';
  }
  await addRecord(activeOwnerUid, activePid, 'conditions', data);
  await refresh();
  return 'added';
}

/** Append a vitals reading — but if a reading already exists at the SAME date+time,
 *  merge the new values into it (one reading per timestamp) instead of a new doc.
 *  Existing values are not overwritten; only missing parameters are added. */
export async function addOrMergeVital(data: Record<string, unknown>): Promise<'added' | 'merged'> {
  if (!activeOwnerUid || !activePid) throw new Error('No active profile');
  const date = String(data.date ?? '');
  const time = String((data as Record<string, unknown>).time ?? '').trim();
  if (time) {
    const p = get(phr);
    let existingId = '';
    const existingFields = new Set<string>();
    for (const s of p.vitals) {
      for (const sample of s.samples) {
        if (sample.date === date && (sample.time || '') === time && sample._id) {
          existingId = sample._id;
          const f = VITAL_FIELD[s.key]; if (f) existingFields.add(f);
        }
      }
    }
    if (existingId) {
      const fields: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(data)) {
        if (k === 'date' || k === 'time' || k === 'provider' || v == null || v === '') continue;
        if (existingFields.has(k)) continue;                 // don't overwrite an existing parameter
        fields[k] = v;
      }
      if (Object.keys(fields).length) { await updateRecord(activeOwnerUid, activePid, 'vitals', existingId, fields); await refresh(); }
      return 'merged';
    }
  }
  await addRecord(activeOwnerUid, activePid, 'vitals', data);
  await refresh();
  return 'added';
}

export type LabMergeResult = {
  date: string;
  filled: string[];                                            // analytes added (were missing for this date)
  conflicts: { analyte: string; kept: unknown; ignored: unknown }[];  // differed → kept existing, warn
  alreadyPresent: number;                                      // analytes skipped (same value on file)
  createdNewPanel: boolean;                                    // had to append a new panel → warn
  panelId: string;                                             // panel written/merged into ('' if nothing added)
};

/** Merge a lab panel by DATE: add only analytes not already recorded for that date
 *  (fill missing), NEVER overwrite an existing value — on a value conflict the
 *  existing one is kept and reported. Newly-filled analytes go into an existing
 *  same-code panel for that date if present, else a new panel. The caller warns the
 *  user about conflicts and about a newly-appended panel. */
export async function addOrMergeLabPanel(data: Record<string, unknown>): Promise<LabMergeResult> {
  if (!activeOwnerUid || !activePid) throw new Error('No active profile');
  const norm = (a: unknown) => String(a ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const date = String(data.date ?? '');
  const code = String(data.code ?? 'LAB');
  const incoming = (Array.isArray(data.results) ? data.results : []) as { analyte?: string; value?: unknown }[];
  // Compare dates by DAY, not string, so "7/2/2026" and "07/02/2026" (parser vs analyzer) match.
  const dayOf = (d: string) => parseDate(d) ?? NaN;
  const targetDay = dayOf(date);
  const sameDay = (d: string) => dayOf(d) === targetDay;

  // Everything already on file for this date (across ALL panels), by analyte.
  const onFile = new Map<string, unknown>();
  const p = get(phr);
  for (const panel of p.labs) {
    if (!sameDay(panel.date)) continue;
    for (const r of panel.results || []) if (r.analyte) onFile.set(norm(r.analyte), (r as { value?: unknown }).value);
  }

  const filled: LabMergeResult['filled'] = [];
  const conflicts: LabMergeResult['conflicts'] = [];
  const toAdd: typeof incoming = [];
  let alreadyPresent = 0;
  for (const r of incoming) {
    if (!r.analyte) continue;
    const k = norm(r.analyte);
    if (!onFile.has(k)) { toAdd.push(r); filled.push(String(r.analyte)); continue; }
    const existing = onFile.get(k);
    if (String(existing ?? '').trim() !== String(r.value ?? '').trim()) conflicts.push({ analyte: String(r.analyte), kept: existing, ignored: r.value });
    else alreadyPresent++;
  }

  let createdNewPanel = false;
  let panelId = '';
  if (toAdd.length) {
    const target = p.labs.find((l) => sameDay(l.date) && l.code === code && l._id);
    if (target && target._id) {
      await updateRecord(activeOwnerUid, activePid, 'labs', target._id, { results: [...(target.results || []), ...toAdd] });
      panelId = target._id;
    } else {
      panelId = await addRecord(activeOwnerUid, activePid, 'labs', { ...data, results: toAdd });
      createdNewPanel = true;
    }
    await refresh();
  } else {
    panelId = p.labs.find((l) => sameDay(l.date) && l.code === code && l._id)?._id ?? '';
  }
  return { date, filled, conflicts, alreadyPresent, createdNewPanel, panelId };
}

/** True if the signed-in user owns the active profile (vs. it being shared to them). */
export const isOwnerOfActive = derived([activeProfile, user], ([$p, $u]) => !!$p && !!$u && $p.ownerUid === $u.uid);
/** Can the signed-in user MANAGE this record (edit data + add/remove caregivers)? —
 *  the owner, or a family co-parent (in guardians[], not a restricted provider). */
export const canManageActive = derived([activeProfile, user], ([$p, $u]) => {
  if (!$p || !$u) return false;
  if ($p.ownerUid === $u.uid) return true;
  const e = ($u.email || '').toLowerCase();
  const gs = ($p.guardians || []).map((x) => String(x).toLowerCase());
  const ps = ($p.providers || []).map((x) => String(x).toLowerCase());
  return !!e && gs.includes(e) && !ps.includes(e);
});

// ── In-place edits (owner only; mutable for now, append-only later) ────────────
// A "manager" of a record: the owner, or a family co-parent (in guardians[] but NOT a
// restricted provider). Managers may edit/append records AND manage who else has access.
// Providers append + edit only their OWN entries (enforced by firestore.rules).
function manages(pid: string | null): boolean {
  if (!uid || !pid) return false;
  const p = get(profiles).find((x) => x.id === pid);
  const own = p ? p.ownerUid : (pid === activePid ? activeOwnerUid : null);
  if (own === uid) return true;
  if (!p) return false;
  const e = (email || '').toLowerCase();
  const gs = (p.guardians || []).map((x) => String(x).toLowerCase());
  const ps = (p.providers || []).map((x) => String(x).toLowerCase());
  return !!e && gs.includes(e) && !ps.includes(e);
}
function canWrite(): boolean { return manages(activePid); }

/** Correct an existing lab value in its panel doc. */
export async function editLabCell(panelId: string, analyte: string, value: number | string): Promise<void> {
  if (!canWrite()) return;
  const panel = get(phr).labs.find((p) => p._id === panelId);
  if (!panel) return;
  const results = (panel.results || []).map((r) => (r.analyte === analyte ? { ...r, value } : r));
  await updateRecord(activeOwnerUid!, activePid!, 'labs', panelId, { results });
  await refresh();
}

/** Add a value for an analyte on a date with no prior result (append to that
 *  date's panel if one exists, else create a self-entered panel). */
export async function addLabCell(fullDate: string, analyte: string, unit: string, refRange: string, value: number | string): Promise<void> {
  if (!canWrite()) return;
  const col = colKey(fullDate);
  const panel = get(phr).labs.find((p) => p._id && colKey(p.date) === col);
  const result = { analyte, value, unit, refRange, flag: '' as const };
  if (panel?._id) {
    await updateRecord(activeOwnerUid!, activePid!, 'labs', panel._id, { results: [...(panel.results || []), result] });
  } else {
    await addRecord(activeOwnerUid!, activePid!, 'labs', { code: 'SELF', name: 'Self-entered', date: fullDate, provider: 'self', results: [result] });
  }
  await refresh();
}

/** Correct an existing vital reading's field (temperature/heartRate/…). */
export async function editVitalCell(docId: string, field: string, value: number): Promise<void> {
  if (!canWrite()) return;
  await updateRecord(activeOwnerUid!, activePid!, 'vitals', docId, { [field]: value });
  await refresh();
}
/** Add a vital reading for a date that has none. */
export async function addVitalCell(fullDate: string, field: string, value: number): Promise<void> {
  if (!canWrite()) return;
  await addRecord(activeOwnerUid!, activePid!, 'vitals', { date: fullDate, [field]: value, provider: 'self' });
  await refresh();
}

/** Edit / add a medication (regimen) doc. */
export async function updateMedication(docId: string, fields: Record<string, unknown>): Promise<void> {
  if (!canWrite()) return;
  await updateRecord(activeOwnerUid!, activePid!, 'medications', docId, fields);
  await refresh();
}
export async function addMedication(fields: Record<string, unknown>): Promise<void> {
  if (!canWrite()) return;
  await addRecord(activeOwnerUid!, activePid!, 'medications', fields);
  await refresh();
}

/** Edit fields on any record (procedure, imaging study, etc.). */
export async function updatePhrRecord(kind: RecordKind, docId: string, fields: Record<string, unknown>): Promise<void> {
  if (!canWrite() || !docId) return;
  await updateRecord(activeOwnerUid!, activePid!, kind, docId, fields);
  await refresh();
}

/** Delete a record (imaging study, procedure, etc.). */
export async function deletePhrRecord(kind: RecordKind, docId: string): Promise<void> {
  if (!canWrite() || !docId) return;
  await deleteRecord(activeOwnerUid!, activePid!, kind, docId);
  await refresh();
}
